import { Lock } from "lucide-react";
import { useId, type ReactNode, type Ref } from "react";

import type { LockReason } from "@/api/hooks";
import { cn } from "@/lib/cn";
import { LOCK_REASON_DETAIL, LOCK_REASON_LABEL } from "@/lib/labels";

import { Badge } from "./Badge";

/**
 * Provenance of a value in the two-layer model.
 *
 * `base` and `locked` come from the pinned base content; `seeded` came from a
 * template at creation time. All three are read-only. ADR 0001 is explicit that
 * read-only, seeded, and locked content must *disable* its controls rather than
 * merely display a badge, so this component owns both the badge and the
 * disabling.
 */
export type FieldProvenance =
  | "base"
  | "locked"
  | "seeded"
  | "adopted"
  | "overridden"
  | "customer-added";

const PROVENANCE: Record<
  FieldProvenance,
  { label: string; tone: "neutral" | "accent" | "warning"; editable: boolean; reason: string }
> = {
  base: {
    label: "Inherited from base",
    tone: "neutral",
    editable: false,
    reason:
      "Base content is read-only in the editor. Changing it requires a separate base-change request.",
  },
  locked: {
    // Refined by `lockReason` when the caller knows it. The contract's
    // `LockReason` has three members and only `safety_floor` is the clinical
    // guard floor; labelling a `lifecycle` lock as a safety floor tells a
    // clinician that a workflow state is a clinical safety rule.
    label: "Locked",
    tone: "warning",
    editable: false,
    reason: "This value is locked and cannot be changed in the editor.",
  },
  seeded: {
    label: "Seeded",
    tone: "neutral",
    editable: false,
    reason: "Seeded content is read-only until it is explicitly adopted.",
  },
  adopted: {
    // An adopted library version replaced the base value at this path. Not
    // "Inherited from base" — the base pack shipped a value here and the
    // project's pin replaced it — and not "Overridden by customer", which is a
    // draft edit undone at the draft; this one is undone at the pin.
    label: "Supplied by adopted library version",
    tone: "accent",
    editable: true,
    reason: "",
  },
  overridden: {
    label: "Overridden by customer",
    tone: "accent",
    editable: true,
    reason: "",
  },
  "customer-added": {
    label: "Customer-added",
    tone: "accent",
    editable: true,
    reason: "",
  },
};

export function isEditableProvenance(provenance: FieldProvenance): boolean {
  return PROVENANCE[provenance].editable;
}

export function provenanceReason(provenance: FieldProvenance): string {
  return PROVENANCE[provenance].reason;
}

/**
 * A badge and reason supplied by the caller instead of derived from
 * `provenance`.
 *
 * Product Settings needs this because it does not use the clinical two-layer
 * model. A product setting's ownership is `sourceOfTruth` — one of six kinds
 * with six different promotion paths — and mapping that onto `base` /
 * `overridden` would tell a clinician that a tenant manifest value is
 * "Inherited from base". #37 exists precisely to stop those two vocabularies
 * being blurred together, so the component takes the descriptor rather than
 * growing a second provenance enum.
 *
 * The accessibility treatment is unchanged either way: a non-editable value is
 * announced as disabled and its reason travels with it.
 */
export interface FieldDescriptor {
  label: string;
  tone: "neutral" | "accent" | "warning";
  editable: boolean;
  reason: string;
  /** Renders the padlock, for a value the service refuses to change. */
  locked?: boolean;
}

type ReadOnlyFieldOwnership =
  | {
      /** Ownership in the clinical two-layer model. */
      provenance: FieldProvenance;
      descriptor?: never;
    }
  | {
      /**
       * Ownership supplied by a caller with a different model, such as Product
       * Settings. Mutually exclusive with `provenance`.
       */
      descriptor: FieldDescriptor;
      provenance?: never;
    };

export type ReadOnlyFieldProps = {
  label: string;
  value: ReactNode;
  /**
   * Why the value is locked, when the selected ownership model marks it locked.
   */
  lockReason?: LockReason | undefined;
  /**
   * DOM id and ref for the value element, so a validation finding can focus it
   * and point `aria-describedby` at it. Supplied by `useFieldAnchor`.
   */
  valueId?: string | undefined;
  valueRef?: Ref<HTMLDivElement> | undefined;
  /** Extra description ids — a finding attached to this field, for instance. */
  describedBy?: string | undefined;
  className?: string;
} & ReadOnlyFieldOwnership;

export function ReadOnlyField(props: ReadOnlyFieldProps) {
  const {
    label,
    value,
    lockReason,
    valueId,
    valueRef,
    describedBy,
    className,
  } = props;
  const resolved =
    props.descriptor !== undefined
      ? props.descriptor
      : PROVENANCE[props.provenance];
  const reasonId = useId();
  const labelId = useId();
  const readOnly = !resolved.editable;

  const locked =
    props.descriptor !== undefined
      ? (props.descriptor.locked ?? false)
      : props.provenance === "locked";
  const badgeLabel =
    locked && lockReason
      ? (LOCK_REASON_LABEL[lockReason] ?? resolved.label)
      : resolved.label;
  const reasonText =
    locked && lockReason
      ? (LOCK_REASON_DETAIL[lockReason] ?? resolved.reason)
      : resolved.reason;

  const describedByIds = [readOnly ? reasonId : null, describedBy ?? null]
    .filter((id): id is string => id !== null)
    .join(" ");

  return (
    <div className={cn("space-y-1.5", className)}>
      <div className="flex items-center gap-2">
        <span id={labelId} className="text-sm font-medium text-foreground">
          {label}
        </span>
        <Badge tone={resolved.tone}>
          {locked ? (
            <Lock aria-hidden="true" className="mr-1 h-3 w-3" />
          ) : null}
          {badgeLabel}
        </Badge>
      </div>

      <div
        ref={valueRef}
        {...(valueId ? { id: valueId } : {})}
        // `textbox` rather than a bare div: `aria-readonly` and `aria-disabled`
        // are not permitted on a generic element, and without a role a screen
        // reader arriving here from a finding's jump control is told nothing
        // about what it landed on. The role also means the label beside the
        // field becomes the value's accessible name rather than loose text.
        role="textbox"
        aria-labelledby={labelId}
        // A read-only value is announced as disabled rather than merely styled
        // differently, and the reason travels with it instead of living in a
        // hover-only tooltip.
        aria-readonly="true"
        aria-disabled={readOnly || undefined}
        aria-describedby={describedByIds || undefined}
        // Focusable only when something can send focus here — a finding's jump
        // control. Without an anchor there is nothing to operate and nothing to
        // arrive, so it stays out of the tab order.
        tabIndex={valueId ? -1 : undefined}
        className={cn(
          "min-h-10 rounded-md border px-3 py-2 text-sm",
          "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring",
          readOnly
            ? "border-border bg-muted text-muted-foreground"
            : "border-input bg-card text-foreground",
        )}
      >
        {value}
      </div>

      {readOnly ? (
        <p id={reasonId} className="text-xs text-muted-foreground">
          {reasonText}
        </p>
      ) : null}
    </div>
  );
}
